import {
  LayerData,
  addLineModeAtom,
  addPointModeAtom,
  addPolygonModeAtom,
  addSurfaceModeAtom,
  elevationProfileToolModeAtom,
  featureNavigationAtom,
  highlightedFeatureIdsAtom,
  selectedFeatureAtom,
  tLayerSettings,
} from '@/atoms';
import {
  alignmentDrapeSourcesAtom,
  alignmentsAtom,
  registerAlignmentsAtom,
  selectedAlignmentKeyAtom,
  unregisterAlignmentsAtom,
  type DrapeSampler,
} from '@/atoms/alignment.atom';
import { pushCommandAtom } from '@/atoms/snapping.atom';
import { useSnapResolver } from '@/components/3D/useSnapResolver';
import type { SnapFeature } from '@/lib/snapping/candidates';
import {
  rainfallAccumulationThresholdAtom,
  rainfallGullyTaggingAtom,
  rainfallSelectedFeatureAtom,
  rainfallSelectedPondIdAtom,
  rainfallVisibleKindsAtom,
  type RainfallVisibleKind,
} from '@/atoms/rainfall.atom';
import { crestPickSuppressedAtom } from '@/atoms/crestLine.atom';
import { stockpileToolModeAtom } from '@/atoms/stockpile.atom';
import { aciToRgb, colours, parseOgrStyle, toThreeMeshBasicParams } from '@/core';
import { alignmentKey } from '@/core/alignment';
import { featureToAlignment, parseFeatureAlignmentProps, type FeatureAlignmentProps } from '@/core/featureAlignment';
import { updateVertexAt, type VertexPath } from '@/core/geometryEdit';
import { NumberWithUnit, toDreiTextProps } from '@/core/style';
import { FeaturesQuery } from '@/graphql/graphql';
import { useFeatureActions } from '@/hooks/useFeatureActions';
import { useLayerSettingsMany } from '@/hooks/useLayerSettings';
import { useTheme } from '@/hooks/useTheme';
import { getLayerTransform } from '@/lib/layerTransform';
import { isPondDrained, rainfallStyleFor, rainfallVisibilityFor } from '@/lib/rainfallStyling';
import { applyVectorSurfaceColors } from '@/lib/surfaceColors';
import { PivotControls, Text } from '@react-three/drei';
import { useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { Suspense, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

// Builds a "pipe" mesh geometry that runs through `points`: one capped
// cylinder per polyline edge, with a sphere at every joint so adjacent
// segments meet smoothly even at sharp angles. Returns null if there
// are fewer than two points or every edge is degenerate (zero length).
//
// We keep cylinder/sphere segment counts modest (8 / 8) — typical line
// features hit hundreds of segments in a single layer and a higher
// tessellation blows up the merged buffer for no visible win at the
// 1-5 cm radii the layer-settings slider exposes.
// Stable per-sampler id so an alignment's content key changes when the TIN it
// drapes onto is replaced (a new sampler ⇒ different Z), but NOT when the same
// sampler is merely handed over again in a new Map.
let drapeSourceSeq = 0;
const drapeSourceIds = new WeakMap<object, number>();
const drapeSourceId = (s: DrapeSampler | null): number => {
  if (!s) return 0;
  let id = drapeSourceIds.get(s);
  if (id === undefined) drapeSourceIds.set(s, (id = ++drapeSourceSeq));
  return id;
};

// Cheap content hash of a GeoJSON geometry's coordinates. `geometry` is a
// fresh object on every refetch, so reference identity is useless; this walks
// the numbers once (much cheaper than JSON.stringify) and folds them into a
// 32-bit rolling hash alongside the vertex count.
const coordinatesKey = (geometry: unknown): string => {
  const geom = geometry as { type?: string; coordinates?: unknown } | null | undefined;
  if (!geom) return '';
  let n = 0;
  let h = 0x811c9dc5;
  // Folded at mm precision — catches any real edit while staying immune to
  // float noise in the round-trip through the API.
  const fold = (v: number) => {
    h = (Math.imul(h, 0x01000193) ^ Math.round(v * 1000)) | 0;
  };
  const visit = (c: unknown): void => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === 'number') {
      n++;
      for (const v of c) if (typeof v === 'number') fold(v);
      return;
    }
    for (const child of c) visit(child);
  };
  visit(geom.coordinates);
  return `${geom.type ?? ''}:${n}:${h}`;
};

const PIPE_RADIAL_SEGMENTS = 8;
const buildPipeGeometry = (points: THREE.Vector3[], radius: number): THREE.BufferGeometry | null => {
  if (points.length < 2 || radius <= 0) return null;
  const parts: THREE.BufferGeometry[] = [];
  const up = new THREE.Vector3(0, 1, 0); // CylinderGeometry default axis
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    const dir = new THREE.Vector3().subVectors(b, a);
    const len = dir.length();
    if (len < 1e-9) continue;
    const cyl = new THREE.CylinderGeometry(radius, radius, len, PIPE_RADIAL_SEGMENTS, 1, true);
    // Translate so the cylinder's bottom sits at the origin (it is
    // centred on Y by default), then rotate the +Y axis onto the
    // segment direction, then move it to the segment's start.
    cyl.translate(0, len / 2, 0);
    const q = new THREE.Quaternion().setFromUnitVectors(up, dir.clone().normalize());
    cyl.applyQuaternion(q);
    cyl.translate(a.x, a.y, a.z);
    parts.push(cyl);
  }
  if (parts.length === 0) return null;
  // Sphere joints: one at every vertex (covers both endpoints and
  // internal joints with a single uniform pass). Cheaper than a
  // separate "endcap vs joint" branch, and the visual cost of the
  // extra two spheres at the ends is negligible.
  for (const p of points) {
    const s = new THREE.SphereGeometry(radius, PIPE_RADIAL_SEGMENTS, PIPE_RADIAL_SEGMENTS / 2);
    s.translate(p.x, p.y, p.z);
    parts.push(s);
  }
  const merged = mergeGeometries(parts, false);
  for (const p of parts) p.dispose();
  return merged;
};

// Type definitions for GeoJSON-like geometry
type Geometry =
  | { type: 'Point'; coordinates: [number, number, number?] }
  | { type: 'MultiPoint'; coordinates: [number, number, number?][] }
  | { type: 'LineString'; coordinates: [number, number, number?][] }
  | { type: 'MultiLineString'; coordinates: [number, number, number?][][] }
  | { type: 'Polygon'; coordinates: [number, number, number?][][] }
  | { type: 'MultiPolygon'; coordinates: [number, number, number?][][][] }
  | { type: 'GeometryCollection'; geometries: Geometry[] };

const EPS = 1e-9;
const uncloseRing = (ring: [number, number, number][]) => {
  if (ring.length < 2) return ring;
  const [x0, y0] = ring[0];
  const [xn, yn] = ring[ring.length - 1];
  return Math.abs(x0 - xn) < EPS && Math.abs(y0 - yn) < EPS ? ring.slice(0, -1) : ring;
};

// Robust 2D segment-segment intersection. Uses signed-area orientation tests
// per CLRS §33; rejects pure shared-endpoint touches (adjacent ring edges
// share a vertex by construction and must not count as intersections).
const orient2D = (px: number, py: number, qx: number, qy: number, rx: number, ry: number) => {
  const v = (qx - px) * (ry - py) - (qy - py) * (rx - px);
  if (v > EPS) return 1;
  if (v < -EPS) return -1;
  return 0;
};
const segmentsCross = (a1: THREE.Vector2, a2: THREE.Vector2, b1: THREE.Vector2, b2: THREE.Vector2) => {
  const o1 = orient2D(a1.x, a1.y, a2.x, a2.y, b1.x, b1.y);
  const o2 = orient2D(a1.x, a1.y, a2.x, a2.y, b2.x, b2.y);
  const o3 = orient2D(b1.x, b1.y, b2.x, b2.y, a1.x, a1.y);
  const o4 = orient2D(b1.x, b1.y, b2.x, b2.y, a2.x, a2.y);
  // Proper crossing: each segment straddles the line of the other.
  if (o1 !== 0 && o2 !== 0 && o3 !== 0 && o4 !== 0 && o1 !== o2 && o3 !== o4) return true;
  // Collinear-overlap or T-intersection where an endpoint lies strictly on
  // the interior of the other segment. We tolerate the endpoint-touches that
  // happen at shared ring vertices (handled by the caller skipping adjacent
  // edge pairs); anything else counts as self-intersecting.
  const onSeg = (px: number, py: number, qx: number, qy: number, rx: number, ry: number) =>
    Math.min(px, rx) - EPS <= qx &&
    qx <= Math.max(px, rx) + EPS &&
    Math.min(py, ry) - EPS <= qy &&
    qy <= Math.max(py, ry) + EPS;
  if (o1 === 0 && onSeg(a1.x, a1.y, b1.x, b1.y, a2.x, a2.y)) return true;
  if (o2 === 0 && onSeg(a1.x, a1.y, b2.x, b2.y, a2.x, a2.y)) return true;
  if (o3 === 0 && onSeg(b1.x, b1.y, a1.x, a1.y, b2.x, b2.y)) return true;
  if (o4 === 0 && onSeg(b1.x, b1.y, a2.x, a2.y, b2.x, b2.y)) return true;
  return false;
};

// Returns true iff the (open, implicitly-closed) ring has no self-intersection.
// O(n²); cheap for typical CHC polygons (<100 verts). Adjacent edge pairs
// share a vertex by construction and are skipped. The seam pair (last edge
// → first edge) likewise shares a vertex and is skipped.
const isRingSimple2D = (ring: THREE.Vector2[]) => {
  const n = ring.length;
  if (n < 4) return true;
  for (let i = 0; i < n; i++) {
    const a1 = ring[i];
    const a2 = ring[(i + 1) % n];
    for (let j = i + 2; j < n; j++) {
      // skip the seam (edge n-1 → 0 shares a vertex with edge 0 → 1)
      if (i === 0 && j === n - 1) continue;
      const b1 = ring[j];
      const b2 = ring[(j + 1) % n];
      if (segmentsCross(a1, a2, b1, b2)) return false;
    }
  }
  return true;
};

// Cache validity by the 3D source-ring reference. `updateVertexAt` produces
// new ring arrays only for the ring that was actually edited, so unchanged
// rings keep the same reference across renders — every drag frame on the
// exterior is a cache hit for every hole, and vice versa. WeakMap handles
// GC automatically when an old ring snapshot drops out of the undo stack.
const ringSimpleCache = new WeakMap<object, boolean>();
const ringSimpleCachedFor = (ring3D: object, project: () => THREE.Vector2[]) => {
  const cached = ringSimpleCache.get(ring3D);
  if (cached !== undefined) return cached;
  const result = isRingSimple2D(project());
  ringSimpleCache.set(ring3D, result);
  return result;
};

export const GeoJson = ({
  features,
  visible,
  flying,
  layers,
  viewerPosition,
  mapOrigin,
}: {
  features: FeaturesQuery['featuresByLayer'][number][];
  /** Shared floating origin (useStableMapOrigin in Viewer). */
  mapOrigin: THREE.Vector3;
  visible?: boolean;
  flying: boolean;
  layers: LayerData[] | null;
  viewerPosition?: THREE.Vector3;
}) => {
  const selectedFeature = useAtomValue(selectedFeatureAtom);
  // Multi-feature highlight set driven by the AttributeTable. Treated
  // as a visual-only flag here: it shares the same accent material as
  // the single-select state but never triggers move/edit gizmos (those
  // remain gated on `selectedFeatureAtom` semantics).
  const highlightedFeatureIds = useAtomValue(highlightedFeatureIdsAtom);
  const [navigationContext, setNavigationContext] = useAtom(featureNavigationAtom);

  // Rainfall analysis: read controls-panel atoms once per render so we
  // can hide / restyle features whose parent layer is a RAINFALL_VECTOR.
  // Features outside rainfall layers fall through unchanged.
  const rainfallVisibleKinds = useAtomValue(rainfallVisibleKindsAtom);
  const rainfallThreshold = useAtomValue(rainfallAccumulationThresholdAtom);
  const rainfallSelectedPondId = useAtomValue(rainfallSelectedPondIdAtom);
  const rainfallGullies = useAtomValue(rainfallGullyTaggingAtom);
  const setRainfallSelectedFeature = useSetAtom(rainfallSelectedFeatureAtom);
  const setRainfallSelectedPondId = useSetAtom(rainfallSelectedPondIdAtom);
  const rainfallLayerIds = useMemo(() => {
    const ids = new Set<string>();
    for (const l of layers ?? []) {
      const meta = (l.metadata ?? {}) as { type?: string };
      if (meta.type === 'RAINFALL_VECTOR') ids.add(l.id);
    }
    return ids;
  }, [layers]);
  const camera = useThree((state) => state.camera);
  const mapOriginArray = useMemo(() => mapOrigin.toArray(), [mapOrigin]);
  const [params] = useSearchParams();
  const { createUpdateHandler } = useFeatureActions();
  const layerSettings = useLayerSettingsMany(layers?.map((l) => l.id) || []);

  // --- Feature alignments → registry -------------------------------------
  // Built from the FULL feature list (not the 35 m `nearbyFeatures` window)
  // so a kilometre-long road is walkable from anywhere.
  const drapeSources = useAtomValue(alignmentDrapeSourcesAtom);
  const registerAlignments = useSetAtom(registerAlignmentsAtom);
  const unregisterAlignments = useSetAtom(unregisterAlignmentsAtom);
  // ownerKey → content key of what is currently registered for that owner.
  // `features` is a fresh array on every refetch even when nothing changed,
  // so without this guard each poll would rebuild + rewrite the registry,
  // handing every consumer brand-new Alignment objects (camera snap-back in
  // the walk toolbar, overlay churn). Only owners whose CONTENT moved are
  // written.
  const registeredOwnersRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    type Candidate = { feature: (typeof features)[number]; props: FeatureAlignmentProps; drape: DrapeSampler | null };
    const byLayer = new Map<string, { items: Candidate[]; keys: string[] }>();
    for (const f of features ?? []) {
      const props = parseFeatureAlignmentProps(f.properties);
      if (!props || !f.layerId) continue;
      const drape = props.drapeLayerId ? (drapeSources.get(props.drapeLayerId) ?? null) : null;
      const transform = getLayerTransform(layerSettings[f.layerId]);
      const transformKey = `${transform.offset.join(',')}|${transform.rotation.join(',')}`;
      const bucket = byLayer.get(f.layerId) ?? byLayer.set(f.layerId, { items: [], keys: [] }).get(f.layerId)!;
      bucket.items.push({ feature: f, props, drape });
      bucket.keys.push(
        `${f.id}|${JSON.stringify(props)}|${coordinatesKey(f.geometry)}|${transformKey}|${drapeSourceId(drape)}`,
      );
    }

    const owners = new Map<string, string>();
    for (const [layerId, { items, keys }] of byLayer) {
      const ownerKey = `feature:${layerId}`;
      const contentKey = keys.join(';');
      owners.set(ownerKey, contentKey);
      // Same geometry, same alignment props, same transform, same drape source
      // → what is registered is already correct. Skip the (costly) rebuild and,
      // more importantly, the registry write.
      if (registeredOwnersRef.current.get(ownerKey) === contentKey) continue;
      const alignments = items
        .map(({ feature: f, props, drape }) =>
          featureToAlignment({
            featureId: f.id,
            layerId,
            serial: f.serial,
            geometry: f.geometry as { type: string; coordinates: unknown },
            props,
            transform: getLayerTransform(layerSettings[layerId]),
            drape,
          }),
        )
        .filter((a): a is NonNullable<typeof a> => !!a);
      registerAlignments({ ownerKey, alignments });
    }
    // No per-change cleanup: registration already REPLACES an owner's whole
    // set, whereas unregistering first would momentarily drop the walked key
    // and make `registerAlignmentsAtom` cancel an in-progress walk on every
    // feature refetch. Only owners that vanished are released here; the
    // unmount effect below clears the rest.
    for (const prev of registeredOwnersRef.current.keys()) if (!owners.has(prev)) unregisterAlignments(prev);
    registeredOwnersRef.current = owners;
  }, [features, layerSettings, drapeSources, registerAlignments, unregisterAlignments]);

  // Release everything this component owns — on unmount only.
  useEffect(() => {
    return () => {
      for (const owner of registeredOwnersRef.current.keys()) unregisterAlignments(owner);
      registeredOwnersRef.current = new Map();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addPointMode = useAtomValue(addPointModeAtom);
  const addLineMode = useAtomValue(addLineModeAtom);
  const addPolygonMode = useAtomValue(addPolygonModeAtom);
  const addSurfaceMode = useAtomValue(addSurfaceModeAtom);
  // While any creation tool is active, drop the Move3DArrows / VertexMover
  // wrappers around the previously-selected feature. Those wrappers mount
  // PivotControls / per-vertex drag gizmos that fight with the picker's
  // pointer listeners and can throw "Cannot assign to read only property
  // 'position'" / "Cannot convert undefined or null to object" the moment
  // the new tool's markers also mount in the same frame.
  const anyCreationToolActive =
    addPointMode.enabled || addLineMode.enabled || addPolygonMode.enabled || addSurfaceMode.enabled;

  // cleanup
  useEffect(() => {
    if (!selectedFeature && navigationContext) {
      setNavigationContext(null);
    }
  }, [selectedFeature, navigationContext, setNavigationContext]);

  // Filter features within 35 meters of camera
  const nearbyFeatures = useMemo(() => {
    if (!features) return [];
    if (flying) return features;

    // Prefer the authoritative viewer position (from atom) over camera.position
    // which can be stale (0,0,5) after a Canvas remount.
    const cameraPosition = viewerPosition ?? camera.position.clone();
    const maxDistance = 35; // meters

    const { x: cx, y: cy, z: cz } = cameraPosition;
    const maxDistanceSq = maxDistance * maxDistance;
    // Walks raw coordinate arrays (any nesting depth) without allocating:
    // this runs over every vertex of the project on camera moves.
    const anyWithin = (coords: unknown): boolean => {
      if (!Array.isArray(coords) || coords.length === 0) return false;
      if (typeof coords[0] === 'number') {
        const dx = coords[0] - cx;
        const dy = (coords[1] as number) - cy;
        const dz = ((coords[2] as number | undefined) ?? 0) - cz;
        return dx * dx + dy * dy + dz * dz <= maxDistanceSq;
      }
      for (const c of coords) if (anyWithin(c)) return true;
      return false;
    };

    return features.filter((feature) => {
      const geometry = feature.geometry as Geometry | null | undefined;
      if (!geometry) return false;
      if (geometry.type === 'GeometryCollection')
        return (geometry.geometries ?? []).some((g) => g.type !== 'GeometryCollection' && anyWithin(g.coordinates));
      return anyWithin(geometry.coordinates);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    features,
    flying,
    viewerPosition?.x,
    viewerPosition?.y,
    viewerPosition?.z,
    // Round to ~1cm grid so the memo only recomputes on meaningful movement
    Math.round(camera.position.x * 100),
    Math.round(camera.position.y * 100),
    Math.round(camera.position.z * 100),
  ]);

  const filterNavigablePointFeatures = useCallback(
    (features: FeaturesQuery['featuresByLayer'][number][], excludeFeatureId?: string) => {
      return features.filter((f) => {
        // Only include Point features
        if (f.geometry?.type !== 'Point') return false;

        // Exclude the feature itself if specified
        if (excludeFeatureId && f.id === excludeFeatureId) return false;

        // Exclude features with z=0
        const z = f.geometry.coordinates[2] || 0;
        if (z === 0) return false;

        // Exclude text features
        if (f.properties.Text) return false;

        return true;
      });
    },
    [],
  );

  const calculateDistance = useCallback(
    (feature1: FeaturesQuery['featuresByLayer'][number], feature2: FeaturesQuery['featuresByLayer'][number]) => {
      if (feature1.geometry?.type !== 'Point' || feature2.geometry?.type !== 'Point') {
        return Infinity;
      }

      const point1 = new THREE.Vector3(...feature1.geometry.coordinates);
      const point2 = new THREE.Vector3(...feature2.geometry.coordinates);

      return point1.distanceTo(point2);
    },
    [],
  );

  const createNavigationContext = useCallback(
    (
      selectedFeature: FeaturesQuery['featuresByLayer'][number],
      allFeatures: FeaturesQuery['featuresByLayer'][number][],
      cameraPos: THREE.Vector3,
    ) => {
      // Only create context for Point features
      if (selectedFeature.geometry?.type !== 'Point') {
        return null;
      }

      // Filter to navigable Point features (excluding the selected one)
      const otherNavigableFeatures = filterNavigablePointFeatures(allFeatures, selectedFeature.id);

      const maxDistance = 0.2;
      const nearbyNavigableFeatures = otherNavigableFeatures.filter((feature) => {
        const distance = calculateDistance(selectedFeature, feature);
        return distance <= maxDistance;
      });

      // Sort by distance from selected feature
      const sortedFeatures = nearbyNavigableFeatures.sort((a, b) => {
        const distA = calculateDistance(selectedFeature, a);
        const distB = calculateDistance(selectedFeature, b);
        return distA - distB;
      });

      // Include the selected feature at the beginning
      const allNavigableFeatures = [selectedFeature, ...sortedFeatures];

      // Only create context if there are at least 2 features
      if (allNavigableFeatures.length < 2) {
        return null;
      }

      return {
        featureIds: allNavigableFeatures.map((f) => f.id),
        currentIndex: 0, // Selected feature is always at index 0
        cameraPosition: cameraPos.toArray() as [number, number, number],
        layerId: selectedFeature.layerId!,
      };
    },
    [filterNavigablePointFeatures, calculateDistance],
  );

  // Handle navigation context index changes to update selected feature
  const updateSelectedFeatureFromNavigation = useAtomCallback(
    useCallback(
      (get, set) => {
        const navContext = get(featureNavigationAtom);
        const currentSelected = get(selectedFeatureAtom);

        if (!navContext || !currentSelected) return;

        // Get the feature ID from the current index
        const targetFeatureId = navContext.featureIds[navContext.currentIndex];

        // If the selected feature already matches, no need to update
        if (currentSelected.id === targetFeatureId) return;

        // Find the feature in nearbyFeatures
        const feature = nearbyFeatures.find((f) => f.id === targetFeatureId);
        if (feature) {
          // Update selected feature with full feature object
          set(selectedFeatureAtom, {
            ...feature,
            onSave: createUpdateHandler(feature.id, feature.layerId!),
          });
        }
      },
      [nearbyFeatures, createUpdateHandler],
    ),
  );

  // Watch for navigation context index changes
  useEffect(() => {
    updateSelectedFeatureFromNavigation();
  }, [navigationContext?.currentIndex, updateSelectedFeatureFromNavigation]);

  const handleClick = useAtomCallback(
    useCallback(
      (get, set, event: ThreeEvent<MouseEvent>, featureIdOverride?: string) => {
        event.stopPropagation();
        if (addPointMode.enabled) return false;
        if (visible === false) return false;
        if (event.delta > 3) return false;
        if (params.get('showMeasurements') === 'true') return false;
        // The crest draw-guide tool picks vertices off a DOM-level `click`
        // on the same canvas, so a click that places a vertex must not also
        // select a feature — same reasoning as the elevation-profile /
        // stockpile / add-* guards below, sharing one definition via
        // `crestPickSuppressedAtom`.
        if (get(crestPickSuppressedAtom)) return false;
        // While the elevation-profile tool is in drawing mode, clicks place
        // polyline vertices on whatever surface is under the cursor — they
        // are NOT feature selections. Suppressing the selection here keeps
        // the click handlers from fighting each other.
        if (get(elevationProfileToolModeAtom).phase === 'drawing') return false;
        // Same story for the stockpile tool — `isPicking` (per StockpileTool)
        // is `enabled && phase !== 'closed'`. We mirror that condition so
        // clicks while picking polygon vertices don't accidentally select
        // a feature underneath. The 'closed' phase (panel open, polygon
        // finalised) keeps feature selection working as normal so the user
        // can still inspect features for context while the panel is up.
        const stockpileMode = get(stockpileToolModeAtom);
        if (stockpileMode.enabled && stockpileMode.phase !== 'closed') return false;
        // Same gating for the AddSurface / AddLine / AddPolygon picking flows:
        // R3F's mesh click handler fires *before* the DOM-level pointerup the
        // tools listen on, so without this guard a click on an existing feature
        // would both select that feature *and* drop a vertex on the picking
        // tool. Selection is allowed in the adjusting phase so the user can
        // still tap nearby features for context while reviewing the draft.
        const surfaceMode = get(addSurfaceModeAtom);
        if (surfaceMode.enabled && surfaceMode.phase !== 'adjusting') return false;
        const lineMode = get(addLineModeAtom);
        if (lineMode.enabled && lineMode.phase === 'picking') return false;
        const polygonMode = get(addPolygonModeAtom);
        if (polygonMode.enabled && polygonMode.phase === 'picking') return false;

        const existingSelectedFeature = get(selectedFeatureAtom);
        const existingNavigationContext = get(featureNavigationAtom);

        // If a feature is already selected with navigation context, don't handle the click
        if (existingSelectedFeature && existingNavigationContext) return false;

        // A batched line object holds many features; its click resolves the
        // hit segment's feature and passes it in.
        const featureId = featureIdOverride ?? event.object.userData.featureId;

        // Don't reset flags when re-clicking the same feature mid-edit. Without
        // this guard, an accidental click on the line between vertex handles in
        // move-mode would silently exit move mode.
        if (
          existingSelectedFeature &&
          existingSelectedFeature.id === featureId &&
          (existingSelectedFeature.isMoving || existingSelectedFeature.isEditing)
        )
          return false;

        const feature = nearbyFeatures.find((f) => f.id === featureId);
        if (feature) {
          set(selectedFeatureAtom, {
            ...feature,
            onSave: createUpdateHandler(feature.id, feature.layerId!),
          });

          // Highlight the picked feature's centreline when it is a registered
          // alignment; otherwise drop whatever was highlighted. Read the
          // registry through `get` so this component never subscribes to the
          // registry it also writes to.
          const aKey = feature.layerId ? alignmentKey(feature.layerId, feature.id) : null;
          set(selectedAlignmentKeyAtom, aKey && get(alignmentsAtom).has(aKey) ? aKey : null);

          // Create navigation context for Point features
          const navigationContext = createNavigationContext(feature, nearbyFeatures, camera.position);
          set(featureNavigationAtom, navigationContext);
        }
      },
      [
        visible,
        nearbyFeatures,
        params,
        createUpdateHandler,
        createNavigationContext,
        camera.position,
        addPointMode.enabled,
      ],
    ),
  );

  // One click handler for every feature object. A per-feature closure would
  // be a new prop on every GeoJson render (which follows the camera), so the
  // memoised FeatureElements would rebuild their geometry each time.
  const featureClickRef = useRef<(event: ThreeEvent<MouseEvent>, featureId?: string) => void>(() => {});
  useEffect(() => {
    featureClickRef.current = (event, featureIdOverride) => {
      const featureId: string | undefined = featureIdOverride ?? event.object.userData.featureId;
      const feature = featureId ? nearbyFeatures.find((f) => f.id === featureId) : undefined;
      // Also surface rainfall features into the controls-panel inspector.
      if (feature && rainfallLayerIds.has(feature.layerId as string)) {
        const props = (feature.properties ?? {}) as Record<string, unknown>;
        const kind = props.kind as RainfallVisibleKind | undefined;
        if (kind) {
          setRainfallSelectedFeature({ kind, properties: props });
          if (kind === 'pond' && typeof props.pondId === 'string') {
            setRainfallSelectedPondId(props.pondId);
          }
        }
      }
      handleClick(event, featureId);
    };
  });
  const onFeatureClick = useCallback(
    (event: ThreeEvent<MouseEvent>, featureId?: string) => featureClickRef.current(event, featureId),
    [],
  );
  const theme = useTheme();

  // Per-layer Z-range, pre-computed once per render so each polygon
  // mesh can map its vertex colors against the layer-wide elevation
  // span (not its own triangle's tiny range, which would saturate
  // every triangle into a full gradient and erase the elevation
  // signal). Only walks features that are polygon-typed AND belong
  // to a layer whose surface mode is non-SOLID — cheap when the
  // feature is off, irrelevant when on. Map indexed by layerId; a
  // layer absent from the map renders SOLID.
  const layerZRanges = useMemo(() => {
    const map = new Map<string, { zMin: number; zMax: number }>();
    if (!features) return map;
    // Pre-flag which layers actually need a Z scan (mode != SOLID).
    const wanted = new Set<string>();
    for (const layerId of Object.keys(layerSettings)) {
      const s = layerSettings[layerId];
      const mode = (s as any)?.vectorSurfaceColorMode;
      if (mode === 'ELEVATION' || mode === 'SLOPE') wanted.add(layerId);
    }
    if (wanted.size === 0) return map;
    for (const f of features) {
      const layerId = f.layerId as string | null | undefined;
      if (!layerId || !wanted.has(layerId)) continue;
      const geom = f.geometry as { type?: string; coordinates?: any } | null | undefined;
      if (!geom) continue;
      // Walk every coordinate triple and update min/max Z for the layer.
      const visit = (coords: any) => {
        if (!coords) return;
        if (typeof coords[0] === 'number') {
          const z = coords[2];
          if (typeof z === 'number' && Number.isFinite(z)) {
            const cur = map.get(layerId) ?? { zMin: Infinity, zMax: -Infinity };
            if (z < cur.zMin) cur.zMin = z;
            if (z > cur.zMax) cur.zMax = z;
            map.set(layerId, cur);
          }
          return;
        }
        for (const c of coords) visit(c);
      };
      visit(geom.coordinates);
    }
    return map;
  }, [features, layerSettings]);

  // Group features by layerId so we can wrap each layer in its own
  // transform group. The current renderer used to render every feature
  // in a single flat group (no per-layer transform) — this split lets
  // the per-layer offset/rotation from `LayerSettings` move features in
  // a layer without affecting features from other layers. Features
  // without a layerId (shouldn't happen in practice but defensively
  // handled) all go into a single 'unassigned' bucket with identity
  // transform.
  // A layer whose features didn't change keeps its previous bucket array, so
  // its memoised LineBatch isn't rebuilt when another layer streams in.
  const layerBucketsRef = useRef(new Map<string, FeaturesQuery['featuresByLayer'][number][]>());
  const featuresByLayer = useMemo(() => {
    const map = new Map<string, FeaturesQuery['featuresByLayer'][number][]>();
    for (const f of nearbyFeatures) {
      const lid = (f.layerId as string | null | undefined) ?? '';
      const bucket = map.get(lid);
      if (bucket) {
        bucket.push(f);
      } else {
        map.set(lid, [f]);
      }
    }
    const previous = layerBucketsRef.current;
    for (const [lid, bucket] of map) {
      const prev = previous.get(lid);
      if (prev && prev.length === bucket.length && prev.every((f, i) => f === bucket[i])) map.set(lid, prev);
    }
    layerBucketsRef.current = map;
    return map;
  }, [nearbyFeatures]);

  const renderFeature = (_feature: FeaturesQuery['featuresByLayer'][number]): React.ReactNode => {
    const { geometry } = _feature;
    if (!geometry) return null;

    // Rainfall: skip rendering if the parent layer is a rainfall
    // VECTOR and the controls-panel state hides this kind / threshold.
    const isRainfallFeature = rainfallLayerIds.has(_feature.layerId as string);
    if (isRainfallFeature) {
      const visible = rainfallVisibilityFor(_feature.properties as Record<string, unknown>, {
        visibleKinds: rainfallVisibleKinds,
        accumulationThreshold: rainfallThreshold,
        selectedPondId: rainfallSelectedPondId,
      });
      if (!visible) return null;
    }

    const feature = selectedFeature && selectedFeature?.id === _feature.id ? selectedFeature : _feature;
    const isSelected = Boolean(selectedFeature && selectedFeature?.id === feature.id);
    const isHighlighted = highlightedFeatureIds.has(feature.id);

    const isEditingGeometry =
      isSelected &&
      !!selectedFeature?.isMoving &&
      (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon');

    // Compute rainfall style override (if any) outside of memo so
    // re-renders track per-feature changes correctly.
    const rainfallDrained =
      isRainfallFeature && isPondDrained(feature.properties as Record<string, unknown>, rainfallGullies);
    const rainfallStyle = isRainfallFeature
      ? rainfallStyleFor(feature.properties as Record<string, unknown>, isSelected, rainfallDrained)
      : null;

    const featureElement = (
      <FeatureElement
        feature={feature}
        key={feature.id}
        mapOrigin={mapOrigin}
        mapOriginArray={mapOriginArray}
        onClick={onFeatureClick}
        selected={isSelected || isHighlighted}
        layerSettings={layerSettings[feature.layerId as string]}
        isEditingGeometry={isEditingGeometry}
        rainfallStyle={rainfallStyle}
        layerZRange={layerZRanges.get(feature.layerId as string) ?? null}
      />
    );

    if (isSelected && selectedFeature?.isMoving && !anyCreationToolActive) {
      if (feature.geometry.type === 'Point') {
        return (
          <Move3DArrows
            position={new THREE.Vector3(...feature.geometry.coordinates)}
            key={`move-${selectedFeature.id}`}>
            {featureElement}
          </Move3DArrows>
        );
      }
      if (
        feature.geometry.type === 'LineString' ||
        feature.geometry.type === 'MultiLineString' ||
        feature.geometry.type === 'Polygon' ||
        feature.geometry.type === 'MultiPolygon'
      ) {
        // Build snap candidates from all features except the one being edited
        // so a vertex doesn't snap to its own geometry.
        const snapFeatures: SnapFeature[] = nearbyFeatures
          .filter((f) => f.id !== selectedFeature.id)
          .map((f) => ({ id: f.id, geometry: f.geometry }));
        return (
          <VertexMover key={`move-${selectedFeature.id}`} geometry={feature.geometry} snapFeatures={snapFeatures}>
            {featureElement}
          </VertexMover>
        );
      }
    }

    return featureElement;
  };

  // Plain CAD lines are most of a survey drawing's features. Draw them as one
  // LineSegments per (layer, colour, opacity) instead of one object and draw
  // call each; anything selected, highlighted, piped or rainfall-styled keeps
  // its own FeatureElement.
  // Selection is applied at render time as a per-layer exclusion key, so a
  // click only rebuilds the batches of the layers holding the old / new pick.
  // Per bucket array (stable while its layer is unchanged, see
  // featuresByLayer), so an unchanged layer keeps its `lines` identity too.
  const splitCache = useMemo(() => {
    void rainfallLayerIds;
    void layerSettings;
    return new WeakMap<object, { lines: Feature3D[]; lineIds: Set<string>; individual: Feature3D[] }>();
  }, [rainfallLayerIds, layerSettings]);
  const splitLayers = useMemo(
    () =>
      Array.from(featuresByLayer.entries()).map(([layerId, layerFeatures]) => {
        let split = splitCache.get(layerFeatures);
        if (!split) {
          const batchable = !rainfallLayerIds.has(layerId) && !layerSettings[layerId]?.vectorLineRenderAsPipe;
          const lines: typeof layerFeatures = [];
          const individual: typeof layerFeatures = [];
          for (const f of layerFeatures) {
            const type = f.geometry?.type;
            const isBatchLine = batchable && (type === 'LineString' || type === 'MultiLineString');
            (isBatchLine ? lines : individual).push(f);
          }
          split = { lines, lineIds: new Set(lines.map((f) => f.id)), individual };
          splitCache.set(layerFeatures, split);
        }
        return { layerId, ...split };
      }),
    [featuresByLayer, rainfallLayerIds, layerSettings, splitCache],
  );

  return (
    <group visible={visible}>
      {splitLayers.map(({ layerId, lines, lineIds, individual }) => {
        const transform = getLayerTransform(layerId ? layerSettings[layerId] : null);
        // Selected / highlighted lines leave the batch for a FeatureElement
        // (accent colour, move / vertex-edit gizmos).
        const excluded = new Set<string>();
        if (selectedFeature && lineIds.has(selectedFeature.id)) excluded.add(selectedFeature.id);
        for (const id of highlightedFeatureIds) if (lineIds.has(id)) excluded.add(id);
        const children = (
          <>
            {lines.length > excluded.size && (
              <LineBatch
                features={lines}
                excludeKey={[...excluded].sort().join(',')}
                layerSettings={layerSettings[layerId]}
                mapOrigin={mapOrigin}
                theme={theme}
                onFeatureClick={onFeatureClick}
              />
            )}
            {excluded.size > 0 && lines.filter((f) => excluded.has(f.id)).map(renderFeature)}
            {individual.map(renderFeature)}
          </>
        );
        // Skip the wrapper entirely when the transform is identity —
        // keeps the scene-graph shape unchanged for layers that don't
        // use the feature, which avoids any subtle behaviour changes
        // for measuring / picking on existing projects.
        if (transform.isIdentity) {
          return <group key={layerId || '_'}>{children}</group>;
        }
        return (
          <group key={layerId || '_'} position={transform.offset} rotation={transform.rotation}>
            {children}
          </group>
        );
      })}
    </group>
  );
};

type Feature3D = FeaturesQuery['featuresByLayer'][number];

// A feature's material before selection / rainfall overrides — shared by
// FeatureElement and LineBatch so batched and individual lines match.
const baseFeatureStyling = (
  feature: Feature3D,
  layerSettings: tLayerSettings | undefined,
  theme: string,
  ogrStyle: ReturnType<typeof parseOgrStyle>,
): THREE.MeshBasicMaterialParameters => {
  let styling: THREE.MeshBasicMaterialParameters = {
    color:
      layerSettings && layerSettings.vectorDefaultColor !== '#000000'
        ? layerSettings.vectorDefaultColor
        : theme === 'light'
          ? colours.white.lightmode
          : colours.white.darkmode,
  };
  if (feature.properties.OGR_STYLE) styling = { ...toThreeMeshBasicParams(ogrStyle) };
  if (feature.properties.CadColor) styling = { ...styling, color: aciToRgb(feature.properties.CadColor || 0) };

  if (feature.properties.TransP) styling = { ...styling, opacity: feature.properties.TransP / 100, transparent: true };
  else if (layerSettings && layerSettings.vectorDefaultOpacity < 1.0)
    styling = { ...styling, opacity: layerSettings.vectorDefaultOpacity, transparent: true };

  if (layerSettings && layerSettings.vectorIsWireframe) styling.wireframe = true;
  return styling;
};

interface LineBatchGroup {
  key: string;
  geometry: THREE.BufferGeometry;
  material: THREE.LineBasicMaterial;
  /** Feature id per segment (segment i = vertices 2i, 2i+1). */
  segmentFeatureIds: string[];
}

export const buildLineBatches = (
  features: Feature3D[],
  layerSettings: tLayerSettings | undefined,
  mapOrigin: THREE.Vector3,
  theme: string,
): LineBatchGroup[] => {
  const ogrStyles = new Map<string, ReturnType<typeof parseOgrStyle>>();
  const groups = new Map<
    string,
    { color: THREE.Color; opacity: number; transparent: boolean; positions: number[]; ids: string[] }
  >();
  for (const feature of features) {
    const geometry = feature.geometry as Geometry;
    // Same visibility rule as FeatureElement: a LineString lying entirely at
    // z = 0 is a 2D drawing and isn't shown in 3D.
    if (geometry.type === 'LineString' && geometry.coordinates.every((c) => c[2] === 0)) continue;
    const lines =
      geometry.type === 'LineString'
        ? [geometry.coordinates]
        : geometry.type === 'MultiLineString'
          ? geometry.coordinates
          : [];
    const ogr: string = feature.properties.OGR_STYLE || '';
    let ast = ogrStyles.get(ogr);
    if (!ast) ogrStyles.set(ogr, (ast = parseOgrStyle(ogr)));
    const styling = baseFeatureStyling(feature, layerSettings, theme, ast);
    const color = new THREE.Color(styling.color ?? 0xffffff);
    const opacity = styling.opacity ?? 1;
    const transparent = !!styling.transparent;
    const key = `${color.getHexString()}|${opacity}|${transparent}`;
    let group = groups.get(key);
    if (!group) groups.set(key, (group = { color, opacity, transparent, positions: [], ids: [] }));
    for (const line of lines) {
      for (let i = 0; i < line.length - 1; i++) {
        const [ax, ay, az = 0] = line[i];
        const [bx, by, bz = 0] = line[i + 1];
        group.positions.push(
          ax - mapOrigin.x,
          ay - mapOrigin.y,
          az - mapOrigin.z,
          bx - mapOrigin.x,
          by - mapOrigin.y,
          bz - mapOrigin.z,
        );
        group.ids.push(feature.id);
      }
    }
  }
  const out: LineBatchGroup[] = [];
  for (const [key, g] of groups) {
    if (g.ids.length === 0) continue;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(g.positions, 3));
    geometry.computeBoundingSphere();
    const material = new THREE.LineBasicMaterial({ color: g.color, opacity: g.opacity, transparent: g.transparent });
    out.push({ key, geometry, material, segmentFeatureIds: g.ids });
  }
  return out;
};

const LineBatch = memo(
  ({
    features,
    excludeKey,
    layerSettings,
    mapOrigin,
    theme,
    onFeatureClick,
  }: {
    features: Feature3D[];
    /** Comma-joined ids of `features` drawn individually instead. */
    excludeKey: string;
    layerSettings?: tLayerSettings;
    mapOrigin: THREE.Vector3;
    theme: string;
    onFeatureClick: (event: ThreeEvent<MouseEvent>, featureId?: string) => void;
  }) => {
    const batches = useMemo(() => {
      const excluded = new Set(excludeKey ? excludeKey.split(',') : []);
      const drawn = excluded.size ? features.filter((f) => !excluded.has(f.id)) : features;
      return buildLineBatches(drawn, layerSettings, mapOrigin, theme);
    }, [features, excludeKey, layerSettings, mapOrigin, theme]);
    useEffect(
      () => () => {
        for (const b of batches) {
          b.geometry.dispose();
          b.material.dispose();
        }
      },
      [batches],
    );
    return (
      <>
        {batches.map((b) => (
          <lineSegments
            key={b.key}
            geometry={b.geometry}
            material={b.material}
            position={mapOrigin.toArray()}
            // `featureId` only marks this as a feature object for scene
            // pickers (SceneContextMenuPicker); clicks resolve the segment.
            userData={{ type: 'LineBatch', featureId: b.segmentFeatureIds[0] }}
            onClick={(event) => onFeatureClick(event, b.segmentFeatureIds[Math.floor((event.index ?? 0) / 2)])}
          />
        ))}
      </>
    );
  },
);
LineBatch.displayName = 'LineBatch';

const FeatureElement: React.FC<{
  feature: FeaturesQuery['featuresByLayer'][number];
  mapOrigin: THREE.Vector3;
  mapOriginArray: [number, number, number];
  onClick: (event: ThreeEvent<MouseEvent>) => void;
  selected: boolean;
  layerSettings?: tLayerSettings;
  isEditingGeometry?: boolean;
  /** Rainfall-analysis per-kind style override. When non-null, takes
   *  precedence over OGR_STYLE / CadColor / layerSettings.vectorDefaultColor. */
  rainfallStyle?: import('@/lib/rainfallStyling').RainfallFeatureStyle | null;
  /** Layer-wide Z range (project CRS, un-shifted) for the elevation
   *  colormap. Null means SOLID mode or a non-polygon layer. */
  layerZRange?: { zMin: number; zMax: number } | null;
}> = memo(
  ({
    feature,
    mapOrigin,
    mapOriginArray,
    onClick,
    selected,
    layerSettings,
    isEditingGeometry,
    rainfallStyle,
    layerZRange,
  }) => {
    const theme = useTheme();
    const { geometry } = feature;
    const userData = useMemo(
      () => ({
        type: geometry.type,
        layerId: feature.layerId,
        featureId: feature.id,
      }),
      [geometry.type, feature.layerId, feature.id],
    );

    const ogrStyle = useMemo(() => parseOgrStyle(feature.properties.OGR_STYLE || ''), [feature.properties.OGR_STYLE]);
    const styling = useMemo(() => {
      let styling = baseFeatureStyling(feature, layerSettings, theme, ogrStyle);

      if (selected) styling = { ...styling, color: colours.geojson.active };

      // Rainfall override comes last — it has the most context (the
      // feature's `kind`, its `flowAccumulationM2`, its `edgeTouching`)
      // and explicitly wants to dominate. Selected colour stays applied
      // because rainfallStyleFor itself respects `selected`.
      if (rainfallStyle) {
        styling = {
          ...styling,
          color: rainfallStyle.color,
          opacity: rainfallStyle.opacity,
          transparent: rainfallStyle.opacity < 1,
          wireframe: rainfallStyle.wireframe ?? styling.wireframe,
        };
      }

      return styling;
    }, [feature, theme, ogrStyle, selected, layerSettings, rainfallStyle]);

    // Pre-calculate commonly used values
    const isPoint = geometry.type === 'Point';
    const pointCoords = isPoint ? geometry.coordinates : null;
    const dx = isPoint && pointCoords ? pointCoords[0] - mapOrigin.x : 0;
    const dy = isPoint && pointCoords ? pointCoords[1] - mapOrigin.y : 0;
    const dz = isPoint && pointCoords ? (pointCoords[2] || 0) - mapOrigin.z : 0;

    // Memoize sphere geometry for Point geometries
    const sphereGeometry = useMemo(() => {
      if (!isPoint || !pointCoords) return null;

      let size = layerSettings?.vectorDefaultPointSize || 0.15;
      if (ogrStyle.parts[0]?.params.find((param) => param.name === 'w' && (param.value as NumberWithUnit).unit === 'g'))
        size = (
          ogrStyle.parts[0].params.find((param) => param.name === 'w' && (param.value as NumberWithUnit).unit === 'g')
            ?.value as NumberWithUnit
        ).value;

      if (feature.properties.SubClasses === 'AcDbEntity:AcDbBlockReference') size = 0.01;

      // todo: cad point size handling Balint?
      if (feature.properties.EntityHandle) size = 0.01;

      const geo = new THREE.SphereGeometry(size, 16, 16);
      geo.translate(dx, dy, dz);
      return geo;
    }, [
      isPoint,
      pointCoords,
      dx,
      dy,
      dz,
      ogrStyle.parts,
      feature.properties.SubClasses,
      layerSettings?.vectorDefaultPointSize,
    ]);

    switch (geometry.type) {
      case 'Point': {
        const z = geometry.coordinates[2] || 0;
        if (z === 0) return null;

        if (feature.properties.Text && feature.properties.OGR_STYLE) {
          const textProps = toDreiTextProps(parseOgrStyle(feature.properties.OGR_STYLE), {
            fallbackText: feature.properties.Text,
          });
          return (
            <Suspense fallback={null}>
              {/* <Billboard follow={true}> */}
              <Text {...textProps} position={geometry.coordinates} material-transparent material-depthTest={false}>
                {textProps.text || ''}
              </Text>
              {/* </Billboard> */}
            </Suspense>
          );
        }

        if (!sphereGeometry) return null;

        return (
          <>
            <mesh onClick={onClick} userData={userData} geometry={sphereGeometry} position={mapOriginArray}>
              <meshBasicMaterial {...styling} />
            </mesh>
          </>
        );
      }

      case 'MultiPoint': {
        return geometry.coordinates.map(([x, y, z = 0], i) => {
          const dx = x - mapOrigin.x;
          const dy = y - mapOrigin.y;
          const dz = z - mapOrigin.z;
          if (z === 0) return null;
          const sphereGeo = new THREE.SphereGeometry(0.15, 16, 16);
          sphereGeo.translate(dx, dy, dz);
          return (
            <mesh
              key={`${feature.id}-${i}`}
              geometry={sphereGeo}
              onClick={onClick}
              userData={userData}
              position={mapOriginArray}>
              <meshBasicMaterial {...styling} />
            </mesh>
          );
        });
      }

      case 'LineString': {
        if (geometry.coordinates.every((coords) => coords[2] === 0)) return null;
        const points = geometry.coordinates.map(
          ([x, y, z = 0]) => new THREE.Vector3(x - mapOrigin.x, y - mapOrigin.y, z - mapOrigin.z),
        );
        // Pipe rendering: when the layer setting is on, swap the 1-pixel
        // <line> primitive for a merged cylinders-plus-sphere-joints
        // BufferGeometry sized by `vectorLineWidth` (diameter, metres).
        // We render a <mesh> in that case so it picks up `meshBasicMaterial`
        // with full opacity / wireframe support, same as polygon styling.
        if (layerSettings?.vectorLineRenderAsPipe) {
          const radius = (layerSettings.vectorLineWidth ?? 0.02) / 2;
          const pipeGeo = buildPipeGeometry(points, radius);
          if (!pipeGeo) return null;
          return (
            <mesh geometry={pipeGeo} onClick={onClick} userData={userData} position={mapOriginArray}>
              <meshBasicMaterial {...styling} side={THREE.DoubleSide} />
            </mesh>
          );
        }
        const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
        return (
          <line geometry={lineGeometry} onClick={onClick} userData={userData} position={mapOriginArray}>
            <lineBasicMaterial {...styling} />
          </line>
        );
      }

      case 'MultiLineString': {
        if (geometry.coordinates.every((coords) => coords[2] === 0)) return null;
        const renderAsPipe = !!layerSettings?.vectorLineRenderAsPipe;
        const pipeRadius = (layerSettings?.vectorLineWidth ?? 0.02) / 2;
        return geometry.coordinates
          .map((lineCoords: any, i: number) => {
            const points = lineCoords.map(
              ([x, y, z = 0]) => new THREE.Vector3(x - mapOrigin.x, y - mapOrigin.y, z - mapOrigin.z),
            );
            if (renderAsPipe) {
              const pipeGeo = buildPipeGeometry(points, pipeRadius);
              if (!pipeGeo) return null;
              return (
                <mesh
                  key={`${feature.id}-${i}`}
                  geometry={pipeGeo}
                  position={mapOriginArray}
                  onClick={onClick}
                  userData={userData}>
                  <meshBasicMaterial {...styling} side={THREE.DoubleSide} />
                </mesh>
              );
            }
            const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
            return (
              <line
                key={`${feature.id}-${i}`}
                geometry={lineGeometry}
                position={mapOriginArray}
                onClick={onClick}
                userData={userData}>
                <lineBasicMaterial {...styling} />
              </line>
            );
          })
          .filter(Boolean);
      }

      case 'Polygon':
      case 'MultiPolygon': {
        const polys = geometry.type === 'Polygon' ? [geometry.coordinates] : (geometry.coordinates as any[]);

        if (polys.some((polyCoords) => !polyCoords[0])) return null;
        if (polys.every((polyCoords) => polyCoords[0].every((coords: number[]) => coords[2] === 0))) return null;

        // todo: if this is slow, all feature rendering calculations shall be put into a useMemo
        return polys.map((polyCoords: any, i: number) => {
          // 1) Open rings (assumes backend already: largest ring first = exterior, holes follow; winding fixed)
          const [exteriorRing, ...holeRings] = (polyCoords as [number, number, number][][]).map(uncloseRing);

          // 2) Triangulate in the polygon's own plane, not in world XY.
          //
          // `THREE.ShapeUtils.triangulateShape` is a 2-D triangulator — if we
          // hand it XY-only vectors, any polygon whose plane is roughly
          // perpendicular to the world XY plane (a vertical wall: surface
          // tool output where pairs of vertices share the same XY and only
          // differ in Z) collapses to a line segment in 2-D, the triangulator
          // returns zero faces, and the surface renders as an empty mesh. The
          // user-reported "sometimes my surfaces aren't visible" was exactly
          // this: walls disappeared, tilted slabs survived because they had
          // distinct XY per vertex.
          //
          // Fix: derive an in-plane 2-D basis (uAxis, vAxis) from the
          // polygon's outer ring (cross-product of the first non-degenerate
          // edge pair gives the plane normal; uAxis is the first edge,
          // normalized; vAxis is normal × uAxis), project every vertex of
          // every ring into that basis, and triangulate in those local
          // coords. The 3-D vertex buffer is built from the original
          // coordinates and the triangulator's indices stay valid because
          // the projection preserves vertex order.
          const exterior3 = exteriorRing.map(([x, y, z = 0]) => new THREE.Vector3(x, y, z));
          // Compute plane basis from exterior. We pick the first edge that
          // gives a non-zero normal when crossed with subsequent edges; this
          // copes with collinear leading vertices.
          let uAxis: THREE.Vector3 | null = null;
          let vAxis: THREE.Vector3 | null = null;
          if (exterior3.length >= 3) {
            const v0 = exterior3[0];
            for (let a = 1; a < exterior3.length - 1 && !vAxis; a++) {
              const e1 = new THREE.Vector3().subVectors(exterior3[a], v0);
              if (e1.lengthSq() < 1e-20) continue;
              for (let b = a + 1; b < exterior3.length; b++) {
                const e2 = new THREE.Vector3().subVectors(exterior3[b], v0);
                const n = new THREE.Vector3().crossVectors(e1, e2);
                if (n.lengthSq() < 1e-20) continue;
                n.normalize();
                uAxis = e1.clone().normalize();
                vAxis = new THREE.Vector3().crossVectors(n, uAxis).normalize();
                break;
              }
            }
          }
          // Degenerate polygon (collinear or fewer than 3 unique vertices):
          // fall back to world-XY projection. The mesh will be empty in this
          // case, which is the right outcome — there's nothing to fill.
          const fallback = !uAxis || !vAxis;
          const origin = exterior3[0] ?? new THREE.Vector3();
          const projectTo2D = ([x, y, z = 0]: [number, number, number?]) => {
            if (fallback) {
              return new THREE.Vector2(x - mapOrigin.x, y - mapOrigin.y);
            }
            const rel = new THREE.Vector3(x - origin.x, y - origin.y, z - origin.z);
            return new THREE.Vector2(rel.dot(uAxis!), rel.dot(vAxis!));
          };
          const exterior2 = exteriorRing.map(projectTo2D);
          const holes2 = holeRings.map((hole) => hole.map(projectTo2D));

          // 3a) Validity check — only while the user is actively editing this
          // polygon's vertices. For saved or uploaded data we always
          // best-effort-triangulate, even if the source is garbage; the
          // worst-case is an ugly mesh, but that's the user's data and we
          // shouldn't hide it.
          //
          // Self-intersecting rings make `triangulateShape` produce visually
          // broken output (overlapping / inverted triangles), which is the
          // "looks bad" symptom users hit during a vertex drag. While editing,
          // we skip the mesh in that case and render a red outline instead so
          // the user sees clearly that the current shape is invalid; once they
          // drag back to a simple ring (or undo), the fill returns
          // automatically.
          if (isEditingGeometry) {
            // Cached per-ring; unchanged rings hit the cache for free, so a
            // drag on the exterior never re-checks any hole, and vice versa.
            // We project only on a cache miss.
            const exteriorOk = ringSimpleCachedFor(exteriorRing, () => exterior2);
            const holesOk = exteriorOk
              ? holeRings.every((hole, hi) => ringSimpleCachedFor(hole, () => holes2[hi]))
              : false;
            if (!exteriorOk || !holesOk) {
              return (
                <group key={`${feature.id}-${i}`} position={mapOriginArray}>
                  {[exteriorRing, ...holeRings].map((ring, ri) => {
                    const points = ring.map(
                      ([x, y, z = 0]) => new THREE.Vector3(x - mapOrigin.x, y - mapOrigin.y, z - mapOrigin.z),
                    );
                    if (points.length > 0) points.push(points[0]); // close for line drawing
                    const lineGeo = new THREE.BufferGeometry().setFromPoints(points);
                    return (
                      <line key={ri} geometry={lineGeo} userData={userData}>
                        <lineBasicMaterial color='#ff3b30' />
                      </line>
                    );
                  })}
                </group>
              );
            }
          }

          // 3b) Triangulate
          const faces = THREE.ShapeUtils.triangulateShape(exterior2, holes2);

          // 4) Build 3D vertex buffer in the same concatenation order (OPEN rings)
          const allRingsOpen = [exteriorRing, ...holeRings];
          const vertCount = allRingsOpen.reduce((n, r) => n + r.length, 0);
          const positions = new Float32Array(vertCount * 3);

          let k = 0;
          for (const ring of allRingsOpen) {
            for (const [x, y, z = 0] of ring) {
              positions[k++] = x - mapOrigin.x;
              positions[k++] = y - mapOrigin.y;
              positions[k++] = z - mapOrigin.z;
            }
          }

          const indices = Uint32Array.from(faces.flat());

          const polyGeo = new THREE.BufferGeometry();
          polyGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
          polyGeo.setIndex(new THREE.BufferAttribute(indices, 1));
          polyGeo.translate(0, 0, 0.0001 * Math.random());

          // Vertex-color the triangle if the layer has elevation/slope
          // coloring on. Layer-wide Z range is supplied by the parent so
          // every triangle in the layer maps against the same gradient
          // (per-triangle min/max would saturate each one's gradient
          // locally and erase the elevation signal). Z range gets
          // mapOrigin.z subtracted so it matches the already-shifted
          // positions.
          const surfaceMode = (layerSettings as any)?.vectorSurfaceColorMode ?? 'SOLID';
          const surfaceColormap = (layerSettings as any)?.vectorSurfaceColormap ?? 'SPECTRAL';
          const useVertexColors =
            (surfaceMode === 'ELEVATION' || surfaceMode === 'SLOPE') &&
            !rainfallStyle && // rainfall styling wins, by design
            !selected; // keep the selection highlight readable
          if (useVertexColors) {
            const zMinShifted = surfaceMode === 'ELEVATION' && layerZRange ? layerZRange.zMin - mapOrigin.z : 0;
            const zMaxShifted = surfaceMode === 'ELEVATION' && layerZRange ? layerZRange.zMax - mapOrigin.z : 1;
            applyVectorSurfaceColors(polyGeo, surfaceMode, surfaceColormap, zMinShifted, zMaxShifted);
          }

          return (
            <mesh
              onClick={onClick}
              userData={userData}
              key={`${feature.id}-${i}`}
              geometry={polyGeo}
              position={mapOriginArray}>
              {useVertexColors ? (
                // MeshBasicMaterial — NOT MeshLambertMaterial. Lambert
                // shades faces by face-normals, but we don't compute
                // normals on the polygon geometry above, so Lambert
                // renders every face unlit (= black) regardless of
                // vertex colors. Basic just samples vertex colors
                // directly, which is what we actually want here.
                // (LandXML uses Lambert because its parser produces
                // proper vertex normals — we don't.)
                //
                // Distinct `key` on each branch is load-bearing — both
                // branches are `<meshBasicMaterial>` at the same JSX
                // position, so without different keys r3f diffs props
                // onto the *same* material instance. The spread in the
                // SOLID branch doesn't include `vertexColors`, so a
                // previous render's `vertexColors=true` survived the
                // switch and the selected polygon rendered black
                // (vertexColors=true + no color attribute = solid black).
                // Forcing separate instances avoids the prop-leak entirely.
                <meshBasicMaterial
                  key='vertex-coloured'
                  vertexColors
                  side={THREE.DoubleSide}
                  transparent={(styling.opacity ?? 1) < 1}
                  opacity={styling.opacity ?? 1}
                  wireframe={styling.wireframe}
                />
              ) : (
                <meshBasicMaterial key='solid' {...styling} side={THREE.DoubleSide} />
              )}
            </mesh>
          );
        });
      }

      case 'GeometryCollection': {
        const geometryCollection = geometry as { type: 'GeometryCollection'; geometries: Geometry[] };
        return (geometryCollection.geometries ?? [])
          .map((geometry: Geometry, i: number) => {
            // Create a temporary feature with the individual geometry
            const tempFeature = {
              ...feature,
              geometry: geometry,
            };
            return (
              <FeatureElement
                key={`${feature.id}-geom-${i}`}
                feature={tempFeature}
                mapOrigin={mapOrigin}
                mapOriginArray={mapOriginArray}
                onClick={onClick}
                selected={selected}
                layerSettings={layerSettings}
                rainfallStyle={rainfallStyle}
                layerZRange={layerZRange}
              />
            );
          })
          .filter(Boolean);
      }

      default:
        return null;
    }
  },
);

FeatureElement.displayName = 'FeatureElement';

const Move3DArrows: React.FC<React.PropsWithChildren<{ position: THREE.Vector3 }>> = ({
  children,
  position: defaultPosition,
}) => {
  const [position, setPosition] = useState<THREE.Vector3>(new THREE.Vector3(0, 0, 0));
  const [selectedFeature, setSelectedFeature] = useAtom(selectedFeatureAtom);
  const pushCommand = useSetAtom(pushCommandAtom);
  const [matrix, setMatrix] = useState<THREE.Matrix4>(new THREE.Matrix4());
  // Capture the point position at drag start for undo.
  const dragStartPosRef = useRef<THREE.Vector3 | null>(null);

  useEffect(() => {
    setPosition(new THREE.Vector3().copy(defaultPosition));
    const m = new THREE.Matrix4();
    m.setPosition(defaultPosition.x, defaultPosition.y, defaultPosition.z);
    setMatrix(m);
  }, [defaultPosition.x, defaultPosition.y, defaultPosition.z]);

  const delta = useMemo(
    () =>
      new THREE.Vector3(position.x - defaultPosition.x, position.y - defaultPosition.y, position.z - defaultPosition.z),
    [position, defaultPosition],
  );

  return (
    <group>
      <PivotControls
        matrix={matrix}
        visible
        autoTransform={false}
        disableRotations
        disableScaling
        onDragStart={() => {
          dragStartPosRef.current = position.clone();
        }}
        onDrag={(_l, __, w: THREE.Matrix4) => {
          const position = new THREE.Vector3();
          const rotation = new THREE.Quaternion();
          w.decompose(position, rotation, new THREE.Vector3());
          setMatrix(w);
          setPosition(position);
        }}
        onDragEnd={() => {
          if (!selectedFeature) return;

          const beforePos = dragStartPosRef.current ?? defaultPosition.clone();
          const afterPos = position.clone();

          setSelectedFeature({
            ...selectedFeature,
            isEditing: true,
            geometry: {
              ...selectedFeature.geometry,
              coordinates: [afterPos.x, afterPos.y, afterPos.z],
            },
          });

          // Push an undo command for the whole-feature translation.
          pushCommand({
            label: 'move feature',
            apply: () =>
              setSelectedFeature((sf) =>
                sf
                  ? {
                      ...sf,
                      isEditing: true,
                      geometry: { ...sf.geometry, coordinates: [afterPos.x, afterPos.y, afterPos.z] },
                    }
                  : sf,
              ),
            invert: () =>
              setSelectedFeature((sf) =>
                sf
                  ? {
                      ...sf,
                      isEditing: true,
                      geometry: { ...sf.geometry, coordinates: [beforePos.x, beforePos.y, beforePos.z] },
                    }
                  : sf,
              ),
          });
          dragStartPosRef.current = null;
        }}></PivotControls>
      <group position={delta}>{children}</group>
    </group>
  );
};

type EditableGeometry =
  | { type: 'LineString'; coordinates: [number, number, number?][] }
  | { type: 'MultiLineString'; coordinates: [number, number, number?][][] }
  | { type: 'Polygon'; coordinates: [number, number, number?][][] }
  | { type: 'MultiPolygon'; coordinates: [number, number, number?][][][] };

type VertexId =
  | { type: 'LineString'; vertexIdx: number }
  | { type: 'MultiLineString'; lineIdx: number; vertexIdx: number }
  | { type: 'Polygon'; ringIdx: number; vertexIdx: number }
  | { type: 'MultiPolygon'; polyIdx: number; ringIdx: number; vertexIdx: number };

// Approximate world-units-per-pixel at 1m from the camera, for a typical 50° FOV
// at 1080p. Used to scale handles so they stay roughly the same size on screen
// regardless of camera distance. Multiplied by distance-to-camera at render
// time. Tuned for "comfortable click target" (~16px diameter).
const HANDLE_SCREEN_SCALE = 0.012;
const HANDLE_MIN = 0.05;
const HANDLE_MAX = 1.5;
const HANDLE_BASE_RADIUS = 1; // sphere geometry uses radius=1; scale prop sizes it.

const sameVertex = (a: VertexId, b: VertexId) => {
  if (a.type !== b.type) return false;
  if (a.type === 'LineString' && b.type === 'LineString') return a.vertexIdx === b.vertexIdx;
  if (a.type === 'MultiLineString' && b.type === 'MultiLineString')
    return a.lineIdx === b.lineIdx && a.vertexIdx === b.vertexIdx;
  if (a.type === 'Polygon' && b.type === 'Polygon') return a.ringIdx === b.ringIdx && a.vertexIdx === b.vertexIdx;
  if (a.type === 'MultiPolygon' && b.type === 'MultiPolygon')
    return a.polyIdx === b.polyIdx && a.ringIdx === b.ringIdx && a.vertexIdx === b.vertexIdx;
  return false;
};

// Returns the count of unique vertices in a polygon ring. If the ring is
// closed (first === last), the trailing duplicate is skipped so we only
// render one handle for that shared corner. updateVertexAt() keeps the
// closing duplicate in sync when index 0 (or the last index) is moved.
const EPS_RING = 1e-9;
const uniqueVertexCount = (ring: [number, number, number?][]) => {
  if (ring.length < 2) return ring.length;
  const [x0, y0] = ring[0];
  const [xn, yn] = ring[ring.length - 1];
  const closed = Math.abs(x0 - xn) < EPS_RING && Math.abs(y0 - yn) < EPS_RING;
  return closed ? ring.length - 1 : ring.length;
};

const matrixAt = (pos: [number, number, number]) => {
  const m = new THREE.Matrix4();
  m.setPosition(pos[0], pos[1], pos[2]);
  return m;
};

const VertexHandle: React.FC<{
  position: [number, number, number];
  active: boolean;
  onClick: () => void;
}> = ({ position, active, onClick }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);
  const gl = useThree((s) => s.gl);

  // Camera-relative scaling: keeps handle size ~constant on screen.
  useFrame((state) => {
    const m = meshRef.current;
    if (!m) return;
    const dist = state.camera.position.distanceTo(m.position);
    let s = dist * HANDLE_SCREEN_SCALE;
    if (s < HANDLE_MIN) s = HANDLE_MIN;
    if (s > HANDLE_MAX) s = HANDLE_MAX;
    if (active || hovered) s *= 1.4;
    m.scale.setScalar(s);
  });

  // Restore default cursor on unmount in case we leave hover state mid-drag.
  useEffect(
    () => () => {
      gl.domElement.style.cursor = '';
    },
    [gl],
  );

  const color = active ? colours.geojson.active : hovered ? '#ffd24a' : '#ffaa00';

  return (
    <mesh
      ref={meshRef}
      position={position}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
        gl.domElement.style.cursor = 'grab';
      }}
      onPointerOut={() => {
        setHovered(false);
        gl.domElement.style.cursor = '';
      }}>
      <sphereGeometry args={[HANDLE_BASE_RADIUS, 16, 16]} />
      <meshBasicMaterial color={color} depthTest={false} transparent opacity={0.92} />
    </mesh>
  );
};

const VertexMover: React.FC<React.PropsWithChildren<{ geometry: EditableGeometry; snapFeatures: SnapFeature[] }>> = ({
  geometry,
  children,
  snapFeatures,
}) => {
  const setSelectedFeature = useSetAtom(selectedFeatureAtom);
  const pushCommand = useSetAtom(pushCommandAtom);
  const [active, setActive] = useState<VertexId | null>(null);
  const [matrix, setMatrix] = useState<THREE.Matrix4>(() => new THREE.Matrix4());
  const isDraggingRef = useRef(false);
  // Snapshot of geometry taken at drag start — used for undo command.
  const dragStartGeometryRef = useRef<EditableGeometry | null>(null);

  // For snapping: need camera + viewport size to project world pos → pixels.
  const { camera, size } = useThree();
  const getSnapFeatures = useCallback(() => snapFeatures, [snapFeatures]);
  const resolve = useSnapResolver(getSnapFeatures);

  const vertices = useMemo(() => {
    const out: { id: VertexId; pos: [number, number, number] }[] = [];
    switch (geometry.type) {
      case 'LineString': {
        geometry.coordinates.forEach(([x, y, z = 0], vertexIdx) => {
          out.push({ id: { type: 'LineString', vertexIdx }, pos: [x, y, z] });
        });
        break;
      }
      case 'MultiLineString': {
        geometry.coordinates.forEach((line, lineIdx) => {
          line.forEach(([x, y, z = 0], vertexIdx) => {
            out.push({ id: { type: 'MultiLineString', lineIdx, vertexIdx }, pos: [x, y, z] });
          });
        });
        break;
      }
      case 'Polygon': {
        geometry.coordinates.forEach((ring, ringIdx) => {
          const n = uniqueVertexCount(ring);
          for (let vertexIdx = 0; vertexIdx < n; vertexIdx++) {
            const [x, y, z = 0] = ring[vertexIdx];
            out.push({ id: { type: 'Polygon', ringIdx, vertexIdx }, pos: [x, y, z] });
          }
        });
        break;
      }
      case 'MultiPolygon': {
        geometry.coordinates.forEach((poly, polyIdx) => {
          poly.forEach((ring, ringIdx) => {
            const n = uniqueVertexCount(ring);
            for (let vertexIdx = 0; vertexIdx < n; vertexIdx++) {
              const [x, y, z = 0] = ring[vertexIdx];
              out.push({ id: { type: 'MultiPolygon', polyIdx, ringIdx, vertexIdx }, pos: [x, y, z] });
            }
          });
        });
        break;
      }
    }
    return out;
  }, [geometry]);

  const activePos = useMemo(() => {
    if (!active) return null;
    return vertices.find((v) => sameVertex(v.id, active))?.pos ?? null;
  }, [active, vertices]);

  // Sync the gizmo matrix with the geometry only when we're NOT actively
  // dragging — during a drag, PivotControls already drives `matrix` via
  // setMatrix(w) in onDrag, and re-deriving from atom state would fight it.
  useEffect(() => {
    if (!activePos || isDraggingRef.current) return;
    setMatrix(matrixAt(activePos));
  }, [activePos?.[0], activePos?.[1], activePos?.[2]]);

  const handleVertexClick = (v: { id: VertexId; pos: [number, number, number] }) => {
    setActive(v.id);
    setMatrix(matrixAt(v.pos));
  };

  const commit = (next: [number, number, number], opts: { editing?: boolean } = {}) => {
    if (!active) return;
    const path: VertexPath = active;
    setSelectedFeature((prev) => {
      if (!prev) return prev;
      const nextGeometry = updateVertexAt(prev.geometry as EditableGeometry, path, next);
      return {
        ...prev,
        ...(opts.editing ? { isEditing: true } : {}),
        geometry: nextGeometry,
      };
    });
  };

  return (
    <group>
      {children}
      {vertices.map((v) => {
        const isActive = active ? sameVertex(v.id, active) : false;
        let key: string;
        switch (v.id.type) {
          case 'LineString':
            key = `ls-${v.id.vertexIdx}`;
            break;
          case 'MultiLineString':
            key = `mls-${v.id.lineIdx}-${v.id.vertexIdx}`;
            break;
          case 'Polygon':
            key = `pg-${v.id.ringIdx}-${v.id.vertexIdx}`;
            break;
          case 'MultiPolygon':
            key = `mpg-${v.id.polyIdx}-${v.id.ringIdx}-${v.id.vertexIdx}`;
            break;
        }
        return <VertexHandle key={key} position={v.pos} active={isActive} onClick={() => handleVertexClick(v)} />;
      })}
      {active && activePos ? (
        <PivotControls
          matrix={matrix}
          visible
          autoTransform={false}
          disableRotations
          disableScaling
          onDragStart={() => {
            isDraggingRef.current = true;
            // Snapshot the current geometry for the global undo command
            // pushed via pushCommandAtom on drag end.
            dragStartGeometryRef.current = geometry;
          }}
          onDrag={(_l, __, w: THREE.Matrix4) => {
            const pos = new THREE.Vector3();
            const rot = new THREE.Quaternion();
            w.decompose(pos, rot, new THREE.Vector3());

            // Project the dragged world position to canvas pixels so the
            // snap resolver can find nearby snap candidates.
            const ndc = pos.clone().project(camera);
            const cursorPx: [number, number] = [
              ((ndc.x + 1) / 2) * size.width,
              ((1 - ndc.y) / 2) * size.height,
            ];
            const snap = resolve(cursorPx, [pos.x, pos.y, pos.z]);
            const [sx, sy, sz] = snap.position;

            // Snap the gizmo matrix to the resolved position so the handle
            // visually tracks the snap target.
            const snappedMatrix = w.clone();
            snappedMatrix.setPosition(sx, sy, sz);
            setMatrix(snappedMatrix);

            // Live-preview: write the snapped vertex into the atom on every
            // drag frame so the line and vertex spheres follow the gizmo. We
            // hold off setting `isEditing` until drag end so the Save button
            // only appears once the user has actually committed a change.
            commit([sx, sy, sz]);
          }}
          onDragEnd={() => {
            isDraggingRef.current = false;
            // Final commit to set the editing flag; geometry already reflects
            // the last snapped onDrag position.
            const pos = new THREE.Vector3();
            const rot = new THREE.Quaternion();
            matrix.decompose(pos, rot, new THREE.Vector3());
            const after: [number, number, number] = [pos.x, pos.y, pos.z];
            commit(after, { editing: true });

            // Push an undo command so the global history (Ctrl+Z via atom) can
            // restore the geometry snapshot taken at drag start.
            const beforeGeometry = dragStartGeometryRef.current;
            if (beforeGeometry && active) {
              const path: VertexPath = active;
              const afterGeometry = updateVertexAt(beforeGeometry as EditableGeometry, path, after);
              pushCommand({
                label: 'move vertex',
                apply: () =>
                  setSelectedFeature((sf) => (sf ? { ...sf, geometry: afterGeometry, isEditing: true } : sf)),
                invert: () =>
                  setSelectedFeature((sf) => (sf ? { ...sf, geometry: beforeGeometry, isEditing: true } : sf)),
              });
            }
            dragStartGeometryRef.current = null;
          }}
        />
      ) : null}
    </group>
  );
};
